// @givmo/board — what the calendar's screens join from reads they already make (CBG-CAL1 slice 1d).
//
// The served shapes carry ids where a screen shows words (the README's D-4): a connection names its
// officer by `owner_user_id` and its meetings by `upcoming_meeting_ids`. The officers' names come from
// the organization's team list (the org console's members read) and the meetings' days and titles
// from Board's own meetings list, both already read on these pages. A name the team list can't
// resolve (someone whose access to the organization ended) reads as the account's address instead:
// data, never a guess.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { OrgBoardMeetingOut } from '@givmo/api-client';
import { useOrgMembersExhausted, type OrgConsoleMemberRow } from '@givmo/org-console';
import { useBoardOperations } from '../board-runtime';
import { decodeMeetingCalendar, isKeptInCalendar } from '../features/meetings/calendar-decode';
import { extractMeetings } from '../features/meetings/meetings-decode';

/** A team member's name, as the console's team list shows it; null when it has none. */
export function memberName(member: OrgConsoleMemberRow): string | null {
  const name = [member.first_name, member.last_name]
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .join(' ')
    .trim();
  return name.length > 0 ? name : null;
}

/** The organization's people, named: by backend user id, and by their address (lowercased). */
export interface OfficerDirectory {
  byId: ReadonlyMap<string, string>;
  byEmail: ReadonlyMap<string, string>;
  /** Each person's address by backend user id (a host that can't hand over the viewer's own). */
  emailById: ReadonlyMap<string, string>;
}

/** The organization's people (the team list, read to exhaustion), by id and by address. */
export function useOfficerDirectory(orgRef: string | null, enabled = true): OfficerDirectory {
  const members = useOrgMembersExhausted(orgRef ?? '', enabled && orgRef !== null);
  return useMemo(() => {
    const byId = new Map<string, string>();
    const byEmail = new Map<string, string>();
    const emailById = new Map<string, string>();
    for (const member of members.data ?? []) {
      if (member.email) emailById.set(member.user_id, member.email.trim());
      const name = memberName(member);
      if (name === null) continue;
      byId.set(member.user_id, name);
      if (member.email) byEmail.set(member.email.trim().toLowerCase(), name);
    }
    return { byId, byEmail, emailById };
  }, [members.data]);
}

/** The organization's people by backend user id → name. */
export function useOfficerNames(
  orgRef: string | null,
  enabled = true,
): ReadonlyMap<string, string> {
  return useOfficerDirectory(orgRef, enabled).byId;
}

/** The organization's meetings by id, and whether the list has loaded. */
export interface MeetingsById {
  byId: ReadonlyMap<string, OrgBoardMeetingOut>;
  /** The list answered. Until it has, nothing may be said about which meetings a calendar sends. */
  loaded: boolean;
  /** The list failed: what a calendar sends is unknown. */
  failed: boolean;
}

/** The organization's meetings by id, off Board's meetings list (the Meetings page's own read). */
export function useMeetingsById(orgRef: string | null, enabled = true): MeetingsById {
  const operations = useBoardOperations();
  const query = useQuery({
    ...operations.officerListBoardMeetingsOptions({ path: { org_ref: orgRef ?? '' } }),
    enabled: enabled && orgRef !== null,
    select: extractMeetings,
  });
  return useMemo(
    () => ({
      byId: new Map((query.data ?? []).map((meeting) => [meeting.id, meeting] as const)),
      loaded: query.isSuccess,
      failed: query.isError,
    }),
    [query.data, query.isError, query.isSuccess],
  );
}

/** Whether a meeting is still ahead and standing (the meetings a calendar still sends). */
export function isUpcomingScheduled(
  meeting: Pick<OrgBoardMeetingOut, 'status' | 'starts_at'>,
  nowMs: number = Date.now(),
): boolean {
  if (meeting.status !== 'scheduled' || !meeting.starts_at) return false;
  const start = Date.parse(meeting.starts_at);
  return !Number.isNaN(start) && start > nowMs;
}

/** Meetings soonest first. */
export function soonestFirst<T extends { starts_at?: string | null }>(meetings: readonly T[]): T[] {
  const at = (meeting: T) => {
    const value = Date.parse(meeting.starts_at ?? '');
    return Number.isNaN(value) ? Number.MAX_SAFE_INTEGER : value;
  };
  return [...meetings].sort((left, right) => at(left) - at(right));
}

/** The meetings a list of ids names, soonest first, skipping any the list doesn't hold. */
export function meetingsNamed(
  ids: readonly string[],
  byId: ReadonlyMap<string, OrgBoardMeetingOut>,
): OrgBoardMeetingOut[] {
  return soonestFirst(
    ids
      .map((id) => byId.get(id))
      .filter((meeting): meeting is OrgBoardMeetingOut => meeting !== undefined),
  );
}

/**
 * The upcoming meetings a connected calendar sends, read off the meetings list itself: each one's
 * `calendar` block names its organizer's address. This is the join for a reader who can't read
 * Board's connections (they need `board_calendar:manage`): an officer's own calendar.
 */
export function meetingsSentFrom(
  address: string,
  meetings: Iterable<OrgBoardMeetingOut>,
  nowMs: number = Date.now(),
): OrgBoardMeetingOut[] {
  const wanted = address.trim().toLowerCase();
  const sent: OrgBoardMeetingOut[] = [];
  for (const meeting of meetings) {
    if (!isUpcomingScheduled(meeting, nowMs)) continue;
    const calendar = decodeMeetingCalendar((meeting as { calendar?: unknown }).calendar);
    if (calendar === null || calendar.channel !== 'org_calendar' || !isKeptInCalendar(calendar)) {
      continue;
    }
    if ((calendar.organizer_email ?? '').trim().toLowerCase() === wanted) sent.push(meeting);
  }
  return soonestFirst(sent);
}
